// PanelResizeHandle — the draggable resize handle for the left rail / right
// dock, pure/props (ui-affordances-resize-reorder-tooltip.md).
//
// A `role="separator"` divider with BOTH a pointer path and a keyboard path
// (arrow keys ± step, double-click / Home to reset), an `aria-valuenow` in px,
// and a labelled orientation. Unlike the pane Sash (which reports FRACTIONS),
// this reports absolute PIXEL growth: `onResizeBy(growPx)` where a positive
// value means "make this panel WIDER". `growSign` folds in which direction is
// "wider" so the host stays a single `width + growPx` clamp regardless of edge:
//   • rail (handle on its RIGHT edge): dragging → END makes it wider → +1;
//   • dock (handle on its LEFT edge): dragging → START makes it wider → -1.
//
// Impurity is confined to this handle's own pointer handlers (px measurement,
// the sanctioned mount-edge bit); the DECISION (clamp, persist) is the pure host
// seam. No buried effects — pointer capture is per-gesture and released on up.

import { useLingui } from "@lingui/react/macro";
import { useRef } from "react";
import "./PanelResizeHandle.css";

// One arrow-key press changes the width by this many px.
const KEY_STEP = 16;

export type PanelResizeHandleProps = {
    // The accessible name (e.g. "Resize sidebar" / "Resize panel").
    readonly label: string;
    // Current width in px — drives aria-valuenow.
    readonly value: number;
    readonly min: number;
    readonly max: number;
    // +1 when dragging toward the END (right, in LTR) widens the panel; -1 when
    // dragging toward the START widens it. Also picks which arrow grows.
    readonly growSign: 1 | -1;
    // Grow the owning panel by `growPx` (may be negative to shrink). The host
    // clamps + persists.
    readonly onResizeBy: (growPx: number) => void;
    // Reset to the default width (double-click / Home).
    readonly onReset: () => void;
    readonly className?: string;
};

export const PanelResizeHandle = ({
    label,
    value,
    min,
    max,
    growSign,
    onResizeBy,
    onReset,
    className,
}: PanelResizeHandleProps) => {
    const { t } = useLingui();
    const lastX = useRef<number | null>(null);

    const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
        e.currentTarget.setPointerCapture(e.pointerId);
        lastX.current = e.clientX;
    };
    const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
        if (lastX.current === null) return;
        const dx = e.clientX - lastX.current;
        lastX.current = e.clientX;
        // dx toward the END is positive; growSign maps that to "wider".
        onResizeBy(dx * growSign);
    };
    const onPointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
        if (lastX.current === null) return;
        lastX.current = null;
        e.currentTarget.releasePointerCapture(e.pointerId);
    };

    // The arrow that WIDENS the panel, and its opposite that narrows it.
    const growArrow = growSign === 1 ? "ArrowRight" : "ArrowLeft";
    const shrinkArrow = growSign === 1 ? "ArrowLeft" : "ArrowRight";
    const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
        // F81 — never shadow a browser chord: a MODIFIED arrow (e.g. Cmd+→ =
        // browser forward-nav) must fall through to the browser. Only bare
        // grow/shrink arrows + Home drive the resize.
        if (e.metaKey || e.ctrlKey || e.altKey) return;
        if (e.key === growArrow) {
            e.preventDefault();
            onResizeBy(KEY_STEP);
        } else if (e.key === shrinkArrow) {
            e.preventDefault();
            onResizeBy(-KEY_STEP);
        } else if (e.key === "Home") {
            e.preventDefault();
            onReset();
        }
    };

    return (
        // biome-ignore lint/a11y/useSemanticElements: role="separator" + aria-valuenow IS the ARIA for a resize handle; no native element exists.
        <div
            role="separator"
            aria-label={label}
            aria-orientation="vertical"
            aria-valuenow={Math.round(value)}
            aria-valuemin={min}
            aria-valuemax={max}
            tabIndex={0}
            title={t`Drag to resize · double-click to reset`}
            className={`bilda-panel-sash${className ? ` ${className}` : ""}`}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onDoubleClick={onReset}
            onKeyDown={onKeyDown}
        />
    );
};
